import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ShieldCheck, Lock, Mail, Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(from, { replace: true });
    else setError(res.error);
  };

  const fill = (r) => {
    const map = {
      admin: { e: "admin@ccams.io", p: "Admin@12345" },
      compliance_officer: { e: "officer@ccams.io", p: "Officer@12345" },
      auditor: { e: "auditor@ccams.io", p: "Auditor@12345" },
      employee: { e: "employee@ccams.io", p: "Employee@12345" },
    }[r];
    setEmail(map.e);
    setPassword(map.p);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">
            Cyber Compliance And Audit Management System
          </h1>
        </div>

        <div className="card-surface p-6 sm:p-8">
          <h2 className="text-lg font-semibold tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Use your work credentials to continue.
          </p>

          {error && (
            <div
              data-testid="login-error"
              className="mt-5 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="mt-1.5 relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-xs text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="login-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 pr-9"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  data-testid="login-toggle-visibility"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Toggle password visibility"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 border-t border-border pt-5">
            <div className="label-eyebrow mb-3">Demo accounts</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { r: "admin", label: "Admin" },
                { r: "compliance_officer", label: "Officer" },
                { r: "auditor", label: "Auditor" },
                { r: "employee", label: "Employee" },
              ].map((x) => (
                <button
                  key={x.r}
                  type="button"
                  data-testid={`demo-fill-${x.r}`}
                  onClick={() => fill(x.r)}
                  className="text-xs h-9 rounded-md border border-border hover:bg-muted transition-colors font-medium"
                >
                  Use {x.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
